import { cn } from '@/lib/utils';
import { Button } from '@startupquickstart/frontend';
import { useEffect, useRef, useState } from 'react';
import { maxMatchCount } from './minigame-logic';
import { parseSlotMachine } from './types';
import { DemoNotConfigured } from './DemoChrome';
import type { DemoChallenge } from './types';

const REEL_PX = 90;
/** Constant reel speed: every reel moves one face every this-many ms. */
const MS_PER_FACE = 55;
/** Full image-bank rotations the first reel makes; each later reel adds one. */
const MIN_CYCLES = 3;

/** Random face index per reel — gives a non-uniform idle state (no fake "win"). */
function randomFaces(count: number, bank: number): number[] {
  return Array.from({ length: count }, () => Math.floor(Math.random() * Math.max(1, bank)));
}

/**
 * Web replica of the mobile slot-machine minigame (preview only).
 *
 * The landed faces are chosen up front, then every reel spins at the *same
 * constant speed* ({@link MS_PER_FACE} per face) and stops at a staggered time —
 * each successive reel travels one extra full rotation, so it spins longer and
 * stops later, like a mechanical machine. Reels spin from their current position
 * and the offset is silently normalized back into range once settled (same
 * visible face, invisible reset). You can spin as many times as you like.
 */
export function SlotMachineDemo({ challenge }: { challenge: DemoChallenge }) {
  const { reelImages, reelCount, winCondition, pointsOnWin, pointsOnLoss } = parseSlotMachine(
    challenge.minigameConfig,
  );
  const bank = reelImages.length;

  // `faces[i]` is the resting face index of reel i; `offsets[i]` is its px
  // translation (= faces[i] * REEL_PX at rest).
  const [faces, setFaces] = useState<number[]>(() => randomFaces(reelCount, bank));
  const [offsets, setOffsets] = useState<number[]>(() =>
    randomFaces(reelCount, bank).map((f) => f * REEL_PX),
  );
  const [durations, setDurations] = useState<number[]>(() => Array(reelCount).fill(0));
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<'win' | 'loss' | null>(null);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const init = randomFaces(reelCount, bank);
    setFaces(init);
    setOffsets(init.map((f) => f * REEL_PX));
    setDurations(Array(reelCount).fill(0));
    setSpinning(false);
    setResult(null);
    return () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
    };
  }, [challenge.id, reelCount, bank]);

  if (bank < 2) {
    return (
      <DemoNotConfigured>
        This slot machine isn&apos;t fully configured yet — at least two reel images are required.
      </DemoNotConfigured>
    );
  }

  // Strip long enough to cover any resting face plus the longest reel's travel
  // (last reel does MIN_CYCLES + reelCount-1 full rotations).
  const stripLen = (MIN_CYCLES + reelCount + 1) * bank;

  const spin = () => {
    if (spinning) return;
    const landed = randomFaces(reelCount, bank);

    // Each reel travels an exact number of faces (full rotations + the offset
    // needed to land on its face). Reel i does one more rotation than reel i-1,
    // so it covers more distance. Duration = faces × MS_PER_FACE keeps the
    // *speed* identical across reels while the stop times stagger.
    const deltaFaces = landed.map((land, i) => {
      const cur = faces[i];
      return (MIN_CYCLES + i) * bank + ((land - cur + bank) % bank);
    });
    const targetOffsets = deltaFaces.map((d, i) => offsets[i] + d * REEL_PX);
    const durs = deltaFaces.map((d) => d * MS_PER_FACE);

    setResult(null);
    setSpinning(true);
    setDurations(durs);
    setOffsets(targetOffsets);

    const outcome: 'win' | 'loss' = maxMatchCount(landed) >= winCondition ? 'win' : 'loss';
    settleTimer.current = setTimeout(() => {
      // Normalize each reel back to its in-range resting offset without
      // animating — same visible face, so the snap is invisible.
      setDurations(Array(reelCount).fill(0));
      setOffsets(landed.map((f) => f * REEL_PX));
      setFaces(landed);
      setSpinning(false);
      setResult(outcome);
    }, Math.max(...durs) + 60);
  };

  return (
    <div className="space-y-4">
      <span className="font-mono text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
        Slot machine
      </span>

      <div className="rounded-2xl border bg-muted/30 p-4">
        <div className="flex justify-center gap-2">
          {offsets.map((offset, reelIndex) => (
            <div
              key={reelIndex}
              className="overflow-hidden rounded-lg border bg-background"
              style={{ width: REEL_PX, height: REEL_PX }}
            >
              <div
                style={{
                  transform: `translateY(${-offset}px)`,
                  transitionProperty: 'transform',
                  // Linear = constant speed; reels stop (cut) at staggered times.
                  transitionTimingFunction: 'linear',
                  transitionDuration: `${durations[reelIndex]}ms`,
                }}
              >
                {Array.from({ length: stripLen }, (_, k) => (
                  <img
                    key={`${reelIndex}-${k}`}
                    src={reelImages[k % bank]}
                    alt=""
                    className="object-cover"
                    style={{ width: REEL_PX, height: REEL_PX }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Fixed-height status line so the layout never shifts between states. */}
        <p
          className={cn(
            'mt-4 flex min-h-[20px] items-center justify-center text-center text-xs font-medium',
            result === 'win' && 'text-emerald-600',
            result === 'loss' && 'text-red-600',
            !result && 'text-muted-foreground',
          )}
        >
          {spinning
            ? 'Spinning…'
            : result === 'win'
              ? `Match! +${pointsOnWin} points`
              : result === 'loss'
                ? `No match.${pointsOnLoss < 0 ? ` ${pointsOnLoss} points.` : ''} Try again.`
                : `Match ${winCondition} of ${reelCount} reels to win`}
        </p>
      </div>

      <Button className="w-full" onClick={spin} disabled={spinning}>
        Spin
      </Button>
    </div>
  );
}
